import { Box, HStack, Text, Tooltip } from "@chakra-ui/react";
import { RiBracesLine } from "@remixicon/react";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";

import CompanyDisplay from "@/common/components/CompanyDisplay";
import DataCell from "@/common/components/DataCell";
import { DataTable } from "@/common/components/DataTable/DataTable";
import { FilterGroupOnChange } from "@/common/components/filters/FilterGroup";
import { FrequencyProgress } from "@/common/components/FrequencyProgress";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import { featureMetricOptions } from "@/common/components/Rule/RuleMetric";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import {
  DataTableParamState,
  useSafeDataTableStateProps,
} from "@/common/hooks/useDataTableParamState";
import { createSortableColumnHelper } from "@/common/types/reactTableHelpers";
import { linkClickModifierHandler } from "@/common/utils/linkClickHandler";
import { AddColumnPopover } from "@/company/components/AddColumnPopover";
import CompaniesEmptyState from "@/company/components/CompaniesEmptyState";
import { ExportCompaniesButton } from "@/company/components/ExportCompaniesButton";
import { SegmentFilters } from "@/company/components/SegmentFilters";
import { useCompaniesData } from "@/company/data/useCompaniesData";
import { defaultCompanyColumns } from "@/company/hooks/useCompaniesTableState";
import type { SegmentAudienceCountState } from "@/company/hooks/useSegmentAudienceCount";
import { FeatureDisplay } from "@/feature/components/FeatureDisplay";
import { useFeatureNamesData } from "@/feature/data/useFeatureNamesData";
import { useStarsFunnelColors } from "@/feature/hooks/useStarsFunnelColors";
import { FeedbackSatisfactionIcon } from "@/feedback/components/FeedbackSatisfaction";
import { systemAttributeFieldKeys } from "@reflag/shared/attributeFilter";
import {
  CompaniesColumn,
  CompaniesColumnState,
  CompaniesSortBy,
  CompanyListItem,
} from "@reflag/shared/companyAPI";
import { FeatureName } from "@reflag/shared/featureAPI";
import { UIFilter } from "@reflag/shared/filter";
import { SegmentDTO } from "@reflag/shared/segmentAPI";
import { ColumnState } from "@reflag/shared/types/columns";
import { FeatureCompanyUrl } from "@reflag/shared/urls";

const MetricHeader = ({
  feature,
  metric,
}: {
  feature: FeatureName;
  metric: string;
}) => {
  const metricOption = featureMetricOptions.find(
    (option) => option.metric === metric,
  );
  return (
    <Tooltip
      label={`${feature.name} • ${metricOption?.label}`}
      openDelay={300}
      hasArrow
    >
      <HStack spacing={1.5}>
        <FeatureDisplay
          feature={feature}
          flexShrink={1}
          iconSize="xs"
          size="sm"
        />
        <Box color="dimmed">{metricOption?.icon}</Box>
      </HStack>
    </Tooltip>
  );
};

type Props = {
  segment: SegmentDTO;
  audienceCount: SegmentAudienceCountState;
  filter: UIFilter;
  tableState: DataTableParamState<CompaniesSortBy, CompaniesColumn>;
  onFilterChange: (filter: UIFilter | undefined) => void;
  toolbarRightActions?: React.ReactNode;
};

export const CompaniesTable = ({
  segment,
  audienceCount,
  filter,
  tableState,
  onFilterChange,
  toolbarRightActions,
}: Props) => {
  const { env } = useEnvironment();
  const { searchQuery, sorting, pageIndex, columnStates } = tableState;

  // We only handle sorting one column for now
  const { id: sortBy, desc: sortDesc } = sorting[0] ?? {};

  const {
    data,
    isLoading,
    isFetching,
    isError: isListError,
    refetch: refetchList,
  } = useCompaniesData(
    {
      sortBy,
      sortOrder:
        sortDesc === true ? "desc" : sortDesc === false ? "asc" : undefined,
      pageIndex,
      filter,
      idNameFilter: searchQuery,
      includeDetailsForFeatureIds: getUniqueFeatureIds(columnStates ?? []),
      includeAllAttributes: true,
      includeAttributes: getAttributeNames(columnStates ?? []),
    },
    true, // Always enabled for companies table
  );
  const { displayedTotalCount } = audienceCount;
  const isTableFetching = isFetching;
  const isError = isListError || audienceCount.status === "error";
  const refetch = () => {
    void refetchList();
    audienceCount.refetch();
  };

  const navigate = useNavigate();

  const { retainedColor } = useStarsFunnelColors();

  const { data: features = [] } = useFeatureNamesData();

  // We don't want to pass along invalid props to the data table
  const safeTableProps = useSafeDataTableStateProps(tableState);

  const columnHelper = createSortableColumnHelper<
    CompanyListItem,
    CompaniesSortBy
  >();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        cell: ({ row }) => {
          return (
            <CompanyDisplay
              company={row.original}
              fontSize="md"
              fontWeight="medium"
            />
          );
        },
      }),
      columnHelper.accessor("id", {
        header: "ID",
        cell: (cell) => <Text fontSize="sm">{cell.getValue()}</Text>,
      }),
      columnHelper.accessor("firstSeen", {
        header: "First seen",
        sortDescFirst: true,
        cell: (cell) => <DataCell type="date" value={cell.getValue()} />,
      }),
      columnHelper.accessor("lastSeen", {
        header: "Last seen",
        sortDescFirst: true,
        cell: (cell) => <DataCell type="date" value={cell.getValue()} />,
      }),
      columnHelper.accessor("feedbackCount", {
        header: "Feedback",
        cell: (cell) => (
          <DataCell type="number" value={cell.getValue().toString()} compact />
        ),
      }),
      columnHelper.accessor("userCount", {
        header: "Users",
        cell: (cell) => (
          <DataCell type="number" value={cell.getValue().toString()} compact />
        ),
      }),
      // unfortunately we need to type this manually as it can't be inferred
      ...tableState.columnStates.flatMap<ColumnDef<CompanyListItem, any>>(
        (columnState) => {
          const [key, id, metric] = columnState.id.split(".");
          if (key !== "featureMetrics") return [];
          const feature = features?.find((f) => f.id === id);
          if (!feature) return [];

          if (metric === "eventCount") {
            return [
              columnHelper.accessor(`featureMetrics.${id}.eventCount`, {
                id: `featureMetrics.${id}.eventCount`,
                maxSize: 200,
                header: () => (
                  <MetricHeader feature={feature} metric={metric} />
                ),
                meta: {
                  isRemovable: true,
                },
                cell: (cell) => (
                  <DataCell type="number" value={cell.getValue()} />
                ),
              }),
            ];
          }

          if (metric === "firstUsed") {
            return [
              columnHelper.accessor(`featureMetrics.${id}.firstUsed`, {
                id: `featureMetrics.${id}.firstUsed`,
                sortDescFirst: true,
                maxSize: 200,
                header: () => (
                  <MetricHeader feature={feature} metric={metric} />
                ),
                meta: {
                  isRemovable: true,
                },
                cell: (cell) => (
                  <DataCell type="date" value={cell.getValue()} />
                ),
              }),
            ];
          }

          if (metric === "lastUsed") {
            return [
              columnHelper.accessor(`featureMetrics.${id}.lastUsed`, {
                id: `featureMetrics.${id}.lastUsed`,
                sortDescFirst: true,
                maxSize: 200,
                header: () => (
                  <MetricHeader feature={feature} metric={metric} />
                ),
                meta: {
                  isRemovable: true,
                },
                cell: (cell) => (
                  <DataCell type="date" value={cell.getValue()} />
                ),
              }),
            ];
          }

          if (metric === "frequency") {
            return [
              columnHelper.accessor(`featureMetrics.${id}.frequency`, {
                id: `featureMetrics.${id}.frequency`,
                maxSize: 200,
                header: () => (
                  <MetricHeader feature={feature} metric={metric} />
                ),
                meta: {
                  isRemovable: true,
                },
                cell: (cell) => {
                  const value = cell.getValue();
                  return value ? (
                    <FrequencyProgress
                      filledColor={retainedColor}
                      frequency={value}
                    />
                  ) : (
                    <NotAvailableCell />
                  );
                },
              }),
            ];
          }

          if (metric === "satisfaction") {
            return [
              columnHelper.accessor(`featureMetrics.${id}.satisfaction`, {
                id: `featureMetrics.${id}.satisfaction`,
                maxSize: 200,
                header: () => (
                  <MetricHeader feature={feature} metric={metric} />
                ),
                meta: {
                  isRemovable: true,
                },
                cell: (cell) => {
                  const value = cell.getValue();
                  return value ? (
                    <FeedbackSatisfactionIcon colored={true} value={value} />
                  ) : (
                    <NotAvailableCell />
                  );
                },
              }),
            ];
          }

          if (metric === "firstExposureAt") {
            return [
              columnHelper.accessor(`featureMetrics.${id}.firstExposureAt`, {
                id: `featureMetrics.${id}.firstExposureAt`,
                sortDescFirst: true,
                maxSize: 200,
                header: () => (
                  <MetricHeader feature={feature} metric={metric} />
                ),
                meta: {
                  isRemovable: true,
                },
                cell: (cell) => (
                  <DataCell type="date" value={cell.getValue()} />
                ),
              }),
            ];
          }

          if (metric === "lastExposureAt") {
            return [
              columnHelper.accessor(`featureMetrics.${id}.lastExposureAt`, {
                id: `featureMetrics.${id}.lastExposureAt`,
                sortDescFirst: true,
                maxSize: 200,
                header: () => (
                  <MetricHeader feature={feature} metric={metric} />
                ),
                meta: {
                  isRemovable: true,
                },
                cell: (cell) => (
                  <DataCell type="date" value={cell.getValue()} />
                ),
              }),
            ];
          }

          if (metric === "exposureCount") {
            return [
              columnHelper.accessor(`featureMetrics.${id}.exposureCount`, {
                id: `featureMetrics.${id}.exposureCount`,
                maxSize: 200,
                header: () => (
                  <MetricHeader feature={feature} metric={metric} />
                ),
                meta: {
                  isRemovable: true,
                },
                cell: (cell) => (
                  <DataCell type="number" value={cell.getValue()} />
                ),
              }),
            ];
          }

          if (metric === "usageCount") {
            return [
              columnHelper.accessor(`featureMetrics.${id}.usageCount`, {
                id: `featureMetrics.${id}.usageCount`,
                maxSize: 200,
                header: () => (
                  <MetricHeader feature={feature} metric={metric} />
                ),
                meta: {
                  isRemovable: true,
                },
                cell: (cell) => (
                  <DataCell type="number" value={cell.getValue()} />
                ),
              }),
            ];
          }

          return [];
        },
      ),
      ...tableState.columnStates.flatMap((columnState) => {
        if (!columnState.id.startsWith("attributes.")) return [];
        const id = columnState.id.replace("attributes.", "");
        return [
          columnHelper.accessor(`attributes.${id}`, {
            id: `attributes.${id}`,
            maxSize: 200,
            header: () => (
              <Tooltip label={id} openDelay={300} hasArrow>
                <HStack spacing={1}>
                  <Box color="dimmed">
                    <RiBracesLine size={12} />
                  </Box>
                  <Text isTruncated>{id}</Text>
                </HStack>
              </Tooltip>
            ),
            meta: {
              isRemovable: true,
            },
            cell: (cell) => (
              <DataCell
                type={data?.metadata.attributeTypes[id] ?? "string"}
                value={cell.getValue()}
              />
            ),
          }),
        ];
      }),
    ],
    [
      columnHelper,
      tableState.columnStates,
      features,
      retainedColor,
      data?.metadata.attributeTypes,
    ],
  );

  const companies = data?.data || [];

  // when adding a new filter on the companies table, add a column in the table when appropriate
  const onFilterChangeWrapper: FilterGroupOnChange = useCallback(
    (filterGroup, filterAction) => {
      if (filterAction.action === "create") {
        let columnId: CompaniesColumn | undefined;
        if (
          filterAction.filter.type === "companyAttribute" &&
          !systemAttributeFieldKeys.includes(filterAction.filter.field)
        ) {
          columnId =
            `attributes.${filterAction.filter.field}` satisfies CompaniesColumn;
        } else if (filterAction.filter.type === "featureMetric") {
          columnId =
            `featureMetrics.${filterAction.filter.featureId}.${filterAction.filter.metric}` as CompaniesColumn;
        }

        // when adding a column in the filter, add it also to the table
        if (columnId && columnStates.every(({ id }) => id !== columnId)) {
          tableState.onColumnStatesChange([
            ...columnStates,
            {
              id: columnId,
              shown: true,
            },
          ]);
        }
      }

      onFilterChange(filterGroup);
    },
    [onFilterChange, columnStates, tableState],
  );

  return (
    <DataTable
      _toolbarLeft={{ wrap: "wrap", alignSelf: "flex-start" }}
      _toolbarRight={{ wrap: "wrap", flex: "1 1 min-content" }}
      columnCustomizerFooter={
        <Box p={2}>
          <AddColumnPopover<CompaniesColumnState>
            allowedTypes={["companyAttribute", "featureMetric"]}
            columnStates={tableState.columnStates}
            setColumnStates={tableState.onColumnStatesChange}
          />
        </Box>
      }
      columns={columns}
      data={companies}
      defaultColumns={defaultCompanyColumns}
      emptyState={
        <CompaniesEmptyState
          filter={filter}
          isError={isError}
          isLoading={isLoading}
          segment={segment}
          onRetry={refetch}
        />
      }
      isFetching={isTableFetching}
      meta={data?.metadata}
      pageSize={20}
      searchPlaceholder="Search by name or ID"
      tableId="companies"
      toolbarLeftActions={
        <SegmentFilters
          columnStates={tableState.columnStates}
          filter={filter}
          onFilterChange={onFilterChangeWrapper}
        />
      }
      toolbarRightActions={
        <>
          {toolbarRightActions}
          <ExportCompaniesButton
            exportParams={{
              filter,
              idNameFilter: tableState.searchQuery,
              sortBy: tableState.sorting[0]?.id,
              sortOrder: tableState.sorting[0]?.desc ? "desc" : "asc",
              columns: tableState.columnStates.map((s) => s.id),
              includeDetailsForFeatureIds: getUniqueFeatureIds(
                tableState.columnStates,
              ),
            }}
          />
        </>
      }
      totalCount={displayedTotalCount}
      variant="clickable"
      canCustomize
      canPaginate
      canSearch
      canSort
      hasPersistedState
      onRowClick={(row, e) => {
        const url = FeatureCompanyUrl(env, row.original.id);
        e.preventDefault();

        linkClickModifierHandler(url, () => {
          navigate(FeatureCompanyUrl(env, row.original.id));
        })(e);
      }}
      {...safeTableProps}
    />
  );
};

function getUniqueFeatureIds(states: ColumnState[]) {
  return states.reduce<string[]>((acc, { id }) => {
    if (id.startsWith("featureMetrics.")) {
      const [_, featureId] = id.split(".");
      if (featureId && !acc.includes(featureId)) {
        acc.push(featureId);
      }
    }
    return acc;
  }, []);
}
function getAttributeNames(states: ColumnState[]) {
  return states.reduce<string[]>((acc, { id }) => {
    if (id.startsWith("attributes.")) {
      const [_, attributeName] = id.split(".");
      if (attributeName && !acc.includes(attributeName)) {
        acc.push(attributeName);
      }
    }
    return acc;
  }, []);
}
